import { useRef, useState } from 'react';

export const CHECKIN_WORK_MAX = 50;

/**
 * The werf's Checkin@work number, editable inline: click → input → Enter or
 * leaving the field saves, Escape cancels. Without a sale order there is nowhere
 * to store it, so it is read-only ("geen order").
 */
export function CheckinWorkField({
  value,
  editable,
  hasSaleOrder,
  busy,
  onSave,
}: {
  value: string | null;
  editable: boolean;
  hasSaleOrder: boolean;
  busy?: boolean;
  onSave: (value: string) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  // Escape ends editing without the blur that follows it saving the draft.
  const cancelled = useRef(false);

  const label = <span className="font-semibold text-td-navy">Checkin@work: </span>;

  if (draft !== null) {
    const commit = () => {
      if (cancelled.current) {
        cancelled.current = false;
        return;
      }
      const next = draft.trim();
      setDraft(null);
      if (next !== (value ?? '')) onSave(next);
    };
    return (
      <p className="mt-1 text-sm flex flex-wrap items-center gap-1">
        {label}
        <input
          autoFocus
          aria-label="Checkin@work-nummer"
          value={draft}
          maxLength={CHECKIN_WORK_MAX}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
            } else if (e.key === 'Escape') {
              cancelled.current = true;
              setDraft(null);
            }
          }}
          onBlur={commit}
          className="font-mono text-sm border border-td-blue rounded px-2 py-0.5 w-56 max-w-full bg-white"
        />
      </p>
    );
  }

  const shown = value
    ? <span className="font-mono font-semibold text-td-navy break-all">{value}</span>
    : <span className="text-td-muted">geen</span>;

  if (!editable || !hasSaleOrder) {
    return (
      <p className="mt-1 text-sm">
        {label}
        {shown}
        {editable && !hasSaleOrder && <span className="ml-2 text-xs text-td-muted">(geen order)</span>}
      </p>
    );
  }

  return (
    <p className="mt-1 text-sm">
      {label}
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          cancelled.current = false;
          setDraft(value ?? '');
        }}
        aria-label={`Checkin@work bewerken (${value ?? 'geen'})`}
        className="rounded px-1 -mx-1 hover:bg-white/70 underline decoration-dotted underline-offset-4 disabled:opacity-60"
      >
        {shown}
      </button>
    </p>
  );
}
